import { apiClient } from './apiClient';
import { httpErrorDetailMessage } from '../utils/httpErrorDetail';
import {
  formatPersistModeNotPatchMessage,
  normalizeControlBridgePersistMode,
  normalizeControlBridgeSotPlane,
  type ControlBridgePersistMode,
  type ControlBridgeSotPlane,
} from './controlBridgePersistMode';

export type ReportIssueSeverity = 'high' | 'medium' | 'low';
export type ReportIssueKind = 'defect' | 'gap' | 'observation';

export interface ReportIssueWriteBase {
  artifactItemCount?: number;
  itemIdSetHash?: string;
}

export interface ReportIssueRequest {
  summary: string;
  description?: string;
  severity?: ReportIssueSeverity;
  issueType?: ReportIssueKind;
  waypointRef?: string;
  linkedActivityRef?: string;
  discoveredBy?: string;
  surface?: string;
  scopeId?: string;
  writeBase?: ReportIssueWriteBase;
}

export interface ReportIssueResponse {
  ok: boolean;
  itemId: string;
  artifactTypeRef?: string;
  currentState?: string;
  workspaceSlug?: string;
  persistMode?: ControlBridgePersistMode;
  sotPlane?: ControlBridgeSotPlane;
  source?: 'db' | 'workspace';
  writeBase?: ReportIssueWriteBase;
}

export class ControlBridgeReportIssueError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'ControlBridgeReportIssueError';
  }
}

/** F2 / U2 / F5: report-issue must land as Postgres patch on hosted-SoT. */
export function assertReportIssuePersistModePatch(response: ReportIssueResponse): void {
  if (response.persistMode === 'patch') return;
  throw new ControlBridgeReportIssueError(
    formatPersistModeNotPatchMessage(
      'Report-issue',
      response.persistMode,
      response.sotPlane
    ),
    'PERSIST_MODE_NOT_PATCH'
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeWriteBase(raw: unknown): ReportIssueWriteBase | undefined {
  const obj = asRecord(raw);
  if (!obj) return undefined;
  const count = obj.artifactItemCount ?? obj.artifact_item_count;
  const hash = obj.itemIdSetHash ?? obj.item_id_set_hash;
  const out: ReportIssueWriteBase = {};
  if (typeof count === 'number') out.artifactItemCount = count;
  if (typeof hash === 'string') out.itemIdSetHash = hash;
  return Object.keys(out).length ? out : undefined;
}

export function normalizeReportIssueResponse(
  data: unknown,
  fallbackItemId: string
): ReportIssueResponse {
  const row = asRecord(data) ?? {};
  const persistMode = normalizeControlBridgePersistMode(row.persistMode ?? row.persist_mode);
  const sourceRaw = row.source;
  return {
    ok: row.ok !== false,
    itemId:
      (typeof row.itemId === 'string' && row.itemId) ||
      (typeof row.item_id === 'string' && row.item_id) ||
      fallbackItemId,
    artifactTypeRef:
      (typeof row.artifactTypeRef === 'string' && row.artifactTypeRef) ||
      (typeof row.artifact_type_ref === 'string' ? row.artifact_type_ref : undefined),
    currentState:
      (typeof row.currentState === 'string' && row.currentState) ||
      (typeof row.current_state === 'string' ? row.current_state : undefined),
    workspaceSlug:
      (typeof row.workspaceSlug === 'string' && row.workspaceSlug) ||
      (typeof row.workspace_slug === 'string' ? row.workspace_slug : undefined),
    persistMode,
    sotPlane: normalizeControlBridgeSotPlane(row.sotPlane ?? row.sot_plane),
    source: sourceRaw === 'db' || sourceRaw === 'workspace' ? sourceRaw : undefined,
    writeBase: normalizeWriteBase(row.writeBase ?? row.write_base ?? row.write_base_model),
  };
}

export async function reportControlBridgeIssue(
  workspaceSlug: string,
  request: ReportIssueRequest
): Promise<ReportIssueResponse> {
  const slug = workspaceSlug.trim();
  const summary = request.summary.trim();
  if (!slug || !summary) {
    throw new ControlBridgeReportIssueError(
      'Workspace slug and summary are required',
      'NOT_CONFIGURED'
    );
  }

  try {
    const { data } = await apiClient.post<unknown>(
      `/kryspace/workspace/${encodeURIComponent(slug)}/report-issue`,
      {
        summary,
        description: request.description?.trim() || undefined,
        severity: request.severity,
        issueType: request.issueType,
        waypointRef: request.waypointRef,
        linkedActivityRef: request.linkedActivityRef,
        discoveredBy: request.discoveredBy,
        surface: request.surface,
        scopeId: request.scopeId,
        writeBase: request.writeBase,
      }
    );
    const normalized = normalizeReportIssueResponse(data, '');
    assertReportIssuePersistModePatch(normalized);
    return normalized;
  } catch (err: unknown) {
    if (err instanceof ControlBridgeReportIssueError) {
      throw err;
    }
    const axiosErr = err as {
      response?: { status?: number; data?: { detail?: unknown } };
      message?: string;
    };
    const detail = axiosErr.response?.data?.detail;
    const status = axiosErr.response?.status;
    const message =
      status === 409
        ? 'Workspace changed while filing — refresh and try again'
        : httpErrorDetailMessage(detail, axiosErr.message || 'Failed to report issue');
    throw new ControlBridgeReportIssueError(
      axiosErr.response?.status === 404 && !detail
        ? 'Report-issue API is not available on this server (restart backend after deploy)'
        : message,
      axiosErr.response?.status === 404 && !detail
        ? 'ROUTE_NOT_FOUND'
        : axiosErr.response?.status === 409
          ? 'WRITE_BASE_STALE'
          : 'REQUEST_FAILED',
      axiosErr.response?.status
    );
  }
}
